/**
 * Delivery domain types for sp-deliveries-mgmt-api, mirroring the subscription
 * read model in `../subscription/subscription-event.ts`.
 *
 * Conventions inherited from SubscriptionCore:
 *   - BIGINT columns are serialized as `string` (JSON `number` truncates > 2^53).
 *   - `timestamptz` / `date` columns are ISO `string`.
 *   - `NUMERIC` money columns are `string` to preserve decimal precision.
 *
 * Enum values match the DB CHECK constraints on `main.deliveries` /
 * `main.delivery_items`.
 */

export enum DeliveryStatus {
    Active = 'ACTIVE',
    Skipped = 'SKIPPED',
    Cancelled = 'CANCELLED',
    Ordered = 'ORDERED',
    Fulfilled = 'FULFILLED',
    Failed = 'FAILED',
    SystemError = 'SYSTEM_ERROR',
    Removed = 'REMOVED',
}

export enum DeliveryItemStatus {
    Active = 'ACTIVE',
    Cancelled = 'CANCELLED',
}

export enum DeliveryItemMovementReason {
    OutOfStock = 'OUT_OF_STOCK',
}

export enum DeliveryFulfillmentMethod {
    Sth = 'STH',
    Bopis = 'BOPIS',
    Sdd = 'SDD',
}

export enum DeliveryPaymentType {
    Cc = 'CC',
    Paypal = 'PAYPAL',
}

/**
 * Snapshot of `main.deliveries`.
 * `addressId`, `paymentInstrumentId`, and `paymentType` live at the delivery
 * level (all items in a delivery share the same payment instrument and address).
 */
export interface DeliveryCore {
    id: string;
    customerId: string;
    site: string;
    scheduledShipDate: string;
    status: DeliveryStatus;
    cancelInfoId: string | null;
    retryPaymentCounter: number;
    cartId: string | null;
    orderId: string | null;
    orderCreationDate: string | null;
    orderSubTotal: string | null;
    orderTaxTotal: string | null;
    orderShippingTotal: string | null;
    orderDiscountTotal: string | null;
    orderTotal: string | null;
    addressId: string | null;
    paymentInstrumentId: string;
    paymentType: DeliveryPaymentType;
    createdAt: string;
    updatedAt: string;
    version: number;
}

/**
 * Snapshot of `main.delivery_items` (a delivery has many items).
 * `subscriptionId` is null for one-time adds (`one_time_add = true`).
 */
export interface DeliveryItem {
    id: string;
    deliveryId: string;
    cartLineItemId?: string | null;
    subscriptionId: string | null;
    sku: string;
    quantity: number;
    status: DeliveryItemStatus;
    cancelInfoId: string | null;
    oneTimeAdd: boolean;
    retryOosCounter: number;
    oosRetryStartedDate: string | null;
    fulfillmentMethod: DeliveryFulfillmentMethod;
    storeId: string | null;
    reminderEmailPrice: string | null;
    offerId: string | null;
    createdAt: string;
    updatedAt: string;
    version: number;
}

export interface Delivery extends DeliveryCore {
    items: DeliveryItem[];
}

export enum DeliveryFulfillmentEventType {
    DeliveryFulfillmentDue = 'DELIVERY_FULFILLMENT_DUE',
}

/**
 * Pub/Sub payload published by Subscription Fulfillment Service for a due
 * delivery. `delivery` is a snapshot at publish time, not live DMA state.
 */
export interface DeliveryFulfillmentEvent {
    schemaVersion: 1;
    eventId: string;
    eventType: DeliveryFulfillmentEventType;
    occurredAt: string;
    delivery: Delivery;
}
